import { CalendarEventSyncState, CalendarTaskSync, Task } from '@griply/types';
import { useCallback, useMemo } from 'react';

export type TaskCalendarSyncControls = {
  // Absent when no calendar could host the task — every surface hides its
  // affordance on that.
  syncState: CalendarEventSyncState | undefined;
  toggleSync: () => void;
  toggleVisibility: () => void;
};

// Binds one task's sync state to its two toggles. The toggles pass the CURRENT
// flag, not the desired one — `useCalendarTaskSync` inverts it — which is the
// detail every call site used to re-derive, and an inversion bug reads as
// correct at a glance.
export const useTaskCalendarSyncControls = (
  task: Task | undefined,
  calendarSync: CalendarTaskSync | undefined,
): TaskCalendarSyncControls => {
  const syncState = task ? calendarSync?.states.get(task.id) : undefined;

  const toggleSync = useCallback(() => {
    if (!task || !syncState) {
      return;
    }

    calendarSync?.onToggleSync(task, syncState.isSynced);
  }, [calendarSync, syncState, task]);

  const toggleVisibility = useCallback(() => {
    if (!task || !syncState) {
      return;
    }

    calendarSync?.onToggleVisibility(task, syncState.isHidden);
  }, [calendarSync, syncState, task]);

  return useMemo(
    () => ({ syncState, toggleSync, toggleVisibility }),
    [syncState, toggleSync, toggleVisibility],
  );
};
